import { logger } from '@/lib/logger';

import { ENV } from '@/environment/client';

import { trackError } from '@/utils/errors';

import { CHAIN_IDS, SUPPORTED_CHAINS, SUPPORTED_CHAINS_BY_CHAIN_ID } from './chain';

/**
 * QuickNode "Multichain" endpoint name. A single endpoint serves every supported chain
 * (EVM chains and Solana) by varying only the network subdomain (and, for a couple of
 * chains, the URL path). We reuse the existing mainnet endpoint, which has Multichain enabled.
 *
 * Auth is unchanged: a rotating RS256 JWT is sent as an `Authorization: Bearer` header
 * (minted by `/api/get-provider-token`, injected by `onFetchRequest` in `utils/wagmi.ts`,
 * or via `generateJWT` in server-side routes). There is no token in the URL.
 */
const QUICKNODE_MULTICHAIN_ENDPOINT =
  ENV.NEXT_PUBLIC_VERCEL_ENV === 'production' ? 'sly-falling-tab' : 'thrilling-frequent-sky';

/**
 * Builds a JWT-protected QuickNode RPC URL for the Multichain endpoint.
 *
 * @param segment - Network subdomain inserted between the endpoint name and `quiknode.pro`
 *   (e.g. `ethereum-mainnet`, `matic`). Naming is irregular across chains, so it is passed
 *   explicitly rather than derived.
 * @param path - URL path; defaults to `/`. Avalanche routes to the C-chain and HyperEVM to
 *   its EVM namespace.
 */
function buildMultichainRpcUrl({ segment, path = '/' }: { segment: string; path?: string }) {
  return `https://${QUICKNODE_MULTICHAIN_ENDPOINT}.${segment}.quiknode.pro${path}`;
}

export const JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID: Record<
  keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID,
  string
> = {
  [CHAIN_IDS.MAINNET]: buildMultichainRpcUrl({ segment: 'ethereum-mainnet' }),
  [CHAIN_IDS.MATIC]: buildMultichainRpcUrl({ segment: 'matic' }),
  [CHAIN_IDS.OPTIMISM]: buildMultichainRpcUrl({ segment: 'optimism' }),
  [CHAIN_IDS.BSC]: buildMultichainRpcUrl({ segment: 'bsc' }),
  [CHAIN_IDS.ARBITRUM]: buildMultichainRpcUrl({ segment: 'arbitrum-mainnet' }),
  [CHAIN_IDS.AVALANCHE]: buildMultichainRpcUrl({
    segment: 'avalanche-mainnet',
    path: '/ext/bc/C/rpc/',
  }),
  [CHAIN_IDS.BASE]: buildMultichainRpcUrl({ segment: 'base-mainnet' }),
  [CHAIN_IDS.SCROLL]: buildMultichainRpcUrl({ segment: 'scroll-mainnet' }),
  [CHAIN_IDS.LINEA]: buildMultichainRpcUrl({ segment: 'linea-mainnet' }),
  [CHAIN_IDS.MANTLE]: buildMultichainRpcUrl({ segment: 'mantle-mainnet' }),
  [CHAIN_IDS.UNICHAIN]: buildMultichainRpcUrl({ segment: 'unichain-mainnet' }),
  [CHAIN_IDS.PLASMA]: buildMultichainRpcUrl({ segment: 'plasma-mainnet' }),
  [CHAIN_IDS.MONAD]: buildMultichainRpcUrl({ segment: 'monad-mainnet' }),
  [CHAIN_IDS.ABSTRACT]: buildMultichainRpcUrl({ segment: 'abstract-mainnet' }),
  [CHAIN_IDS.HYPEREVM]: buildMultichainRpcUrl({ segment: 'hype-mainnet', path: '/evm' }),
  [CHAIN_IDS.ROBINHOOD]: buildMultichainRpcUrl({ segment: 'robinhood-mainnet' }),
  [CHAIN_IDS.INK]: buildMultichainRpcUrl({ segment: 'ink-mainnet' }),
  [CHAIN_IDS.SOLANA]: buildMultichainRpcUrl({ segment: 'solana-mainnet' }),
};

export const JWT_PROTECTED_JSON_RPC_TRACE_HTTP_URL_BY_CHAIN_ID: Partial<
  Record<keyof typeof SUPPORTED_CHAINS_BY_CHAIN_ID, string>
> = {
  [CHAIN_IDS.HYPEREVM]: buildMultichainRpcUrl({ segment: 'hype-mainnet', path: '/nanoreth' }),
};

// default refresh interval for getting price & chart data in milliseconds
export const REFRESH_INTERVAL = 30_000;

// Make sure we have an RPC url defined for all chains
if (process.env.NODE_ENV !== 'test') {
  for (const chain of SUPPORTED_CHAINS) {
    if (!JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID[chain.id]) {
      if (process.env.NODE_ENV === 'production') {
        trackError(new Error(`RPC url missing for chain: ${chain.name}`));
      }
      logger.error(`RPC URL missing for chain: ${chain.name}`);
    }
  }
}
